import React, { useState, useEffect } from 'react';

// Quick label presets for common flowchart connections
const quickLabels = [
  { label: 'Yes', color: '#10b981' },
  { label: 'No', color: '#ef4444' },
  { label: 'True', color: '#10b981' },
  { label: 'False', color: '#ef4444' },
  { label: 'Success', color: '#10b981' },
  { label: 'Error', color: '#ef4444' },
  { label: 'Next', color: '#3b82f6' },
  { label: 'Back', color: '#6b7280' },
];

// Connection colors
const connectionColors = [
  { name: 'Default', value: '#6b7280' },
  { name: 'Green', value: '#10b981' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Yellow', value: '#f59e0b' },
  { name: 'Purple', value: '#8b5cf6' },
];

const EditConnectionModal = ({ isOpen, connection, nodes, onClose, onSave, onDelete }) => {
  const [label, setLabel] = useState('');
  const [style, setStyle] = useState('solid');
  const [color, setColor] = useState('#6b7280');

  useEffect(() => {
    if (connection) {
      setLabel(connection.label || '');
      setStyle(connection.style || 'solid');
      setColor(connection.color || '#6b7280');
    }
  }, [connection]);

  if (!isOpen || !connection) return null;

  const fromNode = nodes.find(n => n.id === connection.from);
  const toNode = nodes.find(n => n.id === connection.to);

  const handleSave = () => {
    onSave(connection.id, { label, style, color });
    onClose();
  };

  const handleQuickLabel = (quickLabel) => {
    setLabel(quickLabel.label);
    setColor(quickLabel.color);
  };

  const handleDelete = () => {
    onDelete(connection.id);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter') {
      handleSave();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit Connection
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Connection info */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex-1 text-center">
              <div className="text-sm text-gray-500 dark:text-gray-400">From</div>
              <div className="font-medium text-gray-900 dark:text-white truncate">
                {fromNode?.text?.split('\n')[0] || 'Unknown'}
              </div>
            </div>
            <svg className="w-6 h-6 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
            <div className="flex-1 text-center">
              <div className="text-sm text-gray-500 dark:text-gray-400">To</div>
              <div className="font-medium text-gray-900 dark:text-white truncate">
                {toNode?.text?.split('\n')[0] || 'Unknown'}
              </div>
            </div>
          </div>

          {/* Quick Labels */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Quick Labels
            </label>
            <div className="flex flex-wrap gap-2">
              {quickLabels.map((ql) => (
                <button
                  key={ql.label}
                  onClick={() => handleQuickLabel(ql)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border-2 ${
                    label === ql.label
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                  style={{ color: ql.color }}
                >
                  {ql.label}
                </button>
              ))}
              <button
                onClick={() => { setLabel(''); setColor('#6b7280'); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border-2 ${
                  label === ''
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 text-gray-500'
                }`}
              >
                None
              </button>
            </div>
          </div>

          {/* Custom Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Custom Label
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Or type your own label..."
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Line Color
            </label>
            <div className="flex gap-2">
              {connectionColors.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setColor(c.value)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === c.value
                      ? 'border-blue-500 scale-110'
                      : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                  }`}
                  style={{ backgroundColor: c.value }}
                  title={c.name}
                />
              ))}
            </div>
          </div>

          {/* Style */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Line Style
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setStyle('solid')}
                className={`flex-1 flex flex-col items-center gap-1 px-3 py-2 border-2 rounded-lg transition-all ${
                  style === 'solid'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <svg className="w-12 h-1" viewBox="0 0 48 4">
                  <line x1="0" y1="2" x2="48" y2="2" stroke={color} strokeWidth="3" />
                </svg>
                <span className="text-xs text-gray-600 dark:text-gray-400">Solid</span>
              </button>
              <button
                onClick={() => setStyle('dashed')}
                className={`flex-1 flex flex-col items-center gap-1 px-3 py-2 border-2 rounded-lg transition-all ${
                  style === 'dashed'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <svg className="w-12 h-1" viewBox="0 0 48 4">
                  <line x1="0" y1="2" x2="48" y2="2" stroke={color} strokeWidth="3" strokeDasharray="6,3" />
                </svg>
                <span className="text-xs text-gray-600 dark:text-gray-400">Dashed</span>
              </button>
              <button
                onClick={() => setStyle('dotted')}
                className={`flex-1 flex flex-col items-center gap-1 px-3 py-2 border-2 rounded-lg transition-all ${
                  style === 'dotted'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <svg className="w-12 h-1" viewBox="0 0 48 4">
                  <line x1="0" y1="2" x2="48" y2="2" stroke={color} strokeWidth="3" strokeDasharray="2,3" />
                </svg>
                <span className="text-xs text-gray-600 dark:text-gray-400">Dotted</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={handleDelete}
            className="px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors font-medium"
          >
            Delete Connection
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditConnectionModal;
